import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	forwardRef,
	useImperativeHandle,
} from "react"
import { AssetMetadata } from "@ncs/ncs-api/portal-apps/point-of-sale/types"
import { Skeleton } from "@material-ui/lab"

// Custom hook for drag handling with proper frame direction logic
const useDrag360 = (
	pixelsPerFrame: number,
	totalFrames: number,
	onFrameChange: (frameChange: number) => void,
	onDragStart: () => void
) => {
	const isDragging = useRef(false)
	const isMouseDown = useRef(false)
	const lastClientX = useRef(0)
	const accumulatedDragDistance = useRef(0)
	const frameUpdateScheduled = useRef(false)
	const dragStartTime = useRef(0)
	const dragThreshold = 5 // pixels - minimum movement to consider it a drag

	// Validate input
	const safePixelsPerFrame = Math.max(1, pixelsPerFrame)

	// Debounced frame update using RAF
	const scheduleFrameUpdate = useCallback(() => {
		if (frameUpdateScheduled.current) return

		frameUpdateScheduled.current = true
		requestAnimationFrame(() => {
			const distance = accumulatedDragDistance.current
			const frameJumps = Math.floor(Math.abs(distance) / safePixelsPerFrame)

			if (frameJumps > 0) {
				// Intuitive direction: right drag = forward, left drag = backward
				const direction = distance > 0 ? 1 : -1
				onFrameChange(direction * frameJumps)

				// Update accumulator with remaining sub-frame distance
				accumulatedDragDistance.current -= direction * frameJumps * safePixelsPerFrame
			}

			frameUpdateScheduled.current = false
		})
	}, [safePixelsPerFrame, onFrameChange])

	const handleDragStart = useCallback((clientX: number) => {
		isMouseDown.current = true
		isDragging.current = false // Start as false, will be set to true if threshold is exceeded
		lastClientX.current = clientX
		accumulatedDragDistance.current = 0
		dragStartTime.current = Date.now()
	}, [])

	const handleDragMove = useCallback(
		(clientX: number) => {
			// Only process movement if mouse is down
			if (!isMouseDown.current) return

			const deltaX = clientX - lastClientX.current
			const totalDistance = Math.abs(accumulatedDragDistance.current + deltaX)

			// If we haven't started dragging yet, check if we've exceeded the threshold
			if (!isDragging.current && totalDistance > dragThreshold) {
				isDragging.current = true
				onDragStart()
			}

			// Only process movement if we're actually dragging
			if (isDragging.current) {
				lastClientX.current = clientX
				accumulatedDragDistance.current += deltaX
				scheduleFrameUpdate()
			}
		},
		[scheduleFrameUpdate, onDragStart, dragThreshold]
	)

	const handleDragEnd = useCallback(() => {
		const wasDragging = isDragging.current
		isDragging.current = false
		isMouseDown.current = false
		// Clear any remaining accumulated distance on drag end
		accumulatedDragDistance.current = 0
		return wasDragging
	}, [])

	return {
		isDragging: () => isDragging.current,
		handleDragStart,
		handleDragMove,
		handleDragEnd,
	}
}

export type Spin360Props = {
	imageUrl: string // main sprite URL
	meta: AssetMetadata

	// Sizing
	width?: number
	height?: number

	// Behavior
	autoplay?: boolean
	fps?: number
	dragSensitivity?: number
	initialFrame?: number

	// Placeholder / loading
	placeholderUrl?: string // tiny (LQIP) placeholder URL or data URI
	blurUntilLoaded?: boolean // blur the placeholder until sprite fully loads (default true)
	showSkeleton?: boolean // show skeleton while loading (default true)

	className?: string
	style?: CSSProperties
	ariaLabel?: string

	// Optional click handler
	onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
}

export type Spin360Handle = {
	play: () => void
	pause: () => void
	toggle: () => void
	goToFrame: (index: number) => void
	currentFrame: () => number
	isPlaying: () => boolean
	isLoaded: () => boolean
}

export const Spin360 = forwardRef<Spin360Handle, Spin360Props>(function Spin360(
	{
		imageUrl,
		meta,
		width,
		height,
		autoplay = false,
		fps = 12,
		dragSensitivity = 6,
		initialFrame = 0,
		placeholderUrl,
		blurUntilLoaded = true,
		showSkeleton = true,
		className,
		style,
		ariaLabel = "360° product spin",
		onClick,
	},
	ref
) {
	const { rows, columns, totalFrames, layout, frameDimm } = meta || {}

	// Input validation - check after destructuring to avoid hook violations
	const hasValidInputs =
		meta && imageUrl && Number(totalFrames) > 0 && Number(rows) > 0 && Number(columns) > 0

	// Validate and sanitize dragSensitivity
	const pixelsPerFrame = Math.max(1, Math.min(50, dragSensitivity)) // Clamp between 1-50

	if (Number(totalFrames) > 1000) {
		console.warn("Spin360: Too many frames, performance may suffer")
	}

	if (dragSensitivity <= 0) {
		console.warn("Spin360: dragSensitivity must be positive, using default value of 6")
	}

	// Frame sizing - use safe defaults when invalid
	const naturalFrameW =
		frameDimm?.[0] ??
		Math.round((meta?.spriteWidth || 100) / Math.max(1, Number(columns) || 1))
	const naturalFrameH =
		frameDimm?.[1] ?? Math.round((meta?.spriteHeight || 100) / Math.max(1, Number(rows) || 1))
	const resolvedWidth = width ?? naturalFrameW
	const resolvedHeight = height ?? Math.round(resolvedWidth * (naturalFrameH / naturalFrameW))

	// State
	const [frame, setFrame] = useState<number>(
		Math.min(Math.max(0, initialFrame), (Number(totalFrames) || 1) - 1)
	)
	const [loaded, setLoaded] = useState<boolean>(false)

	// Track if we just finished dragging to prevent click
	const justFinishedDragging = useRef(false)

	// Animation
	const playingRef = useRef<boolean>(false)
	const rafRef = useRef<number | null>(null)
	const lastTsRef = useRef<number | null>(null)
	const frameIntervalMs = useMemo(() => 1000 / Math.max(1, fps), [fps])
	const mountedRef = useRef<boolean>(true)

	// Frame change handler for drag operations
	const handleFrameChange = useCallback(
		(frameChange: number) => {
			if (!mountedRef.current) return
			setFrame((prev) => {
				const newFrame = prev + frameChange
				const safeFrameCount = Number(totalFrames) || 1
				return ((newFrame % safeFrameCount) + safeFrameCount) % safeFrameCount
			})
		},
		[totalFrames]
	)

	// Memoize sprite dimensions to avoid recalculation
	const spriteDimensions = useMemo(() => {
		const cols = layout === "horizontal" ? Number(totalFrames) || 1 : Number(columns) || 1
		const rowsCount = layout === "horizontal" ? 1 : Number(rows) || 1
		return { cols, rowsCount }
	}, [layout, totalFrames, columns, rows])

	// Background sizing for the sprite (scaled to match container)
	const bgSize = useMemo(() => {
		const { cols, rowsCount } = spriteDimensions
		return `${cols * resolvedWidth}px ${rowsCount * resolvedHeight}px`
	}, [spriteDimensions, resolvedWidth, resolvedHeight])

	// Compute background-position from frame index
	const frameToOffset = useCallback(
		(idx: number) => {
			const { cols, rowsCount } = spriteDimensions
			const col = idx % cols
			const row = Math.floor(idx / cols)
			const clampedCol = Math.min(Math.max(0, col), cols - 1)
			const clampedRow = Math.min(Math.max(0, row), rowsCount - 1)
			return {
				x: -clampedCol * resolvedWidth,
				y: -clampedRow * resolvedHeight,
			}
		},
		[spriteDimensions, resolvedWidth, resolvedHeight]
	)

	const bgPosition = useMemo(() => {
		const { x, y } = frameToOffset(frame)
		return `${x}px ${y}px`
	}, [frame, frameToOffset])

	// Preload full sprite
	useEffect(() => {
		let cancelled = false
		let img: HTMLImageElement | null = new Image()

		const handleLoad = () => {
			if (!cancelled && mountedRef.current) {
				setLoaded(true)
			}
			cleanup()
		}

		const handleError = () => {
			if (!cancelled && mountedRef.current) {
				setLoaded(false)
			}
			cleanup()
		}

		const cleanup = () => {
			if (img) {
				img.onload = null
				img.onerror = null
				img = null
			}
		}

		img.decoding = "async"
		img.loading = "eager"
		img.onload = handleLoad
		img.onerror = handleError
		img.src = imageUrl

		return () => {
			cancelled = true
			cleanup()
		}
	}, [imageUrl])

	// Animation loop
	const tick = useCallback(
		(ts: number) => {
			// Safety check: component must be mounted and playing
			if (!mountedRef.current || !playingRef.current) {
				rafRef.current = null
				lastTsRef.current = null
				return
			}
			if (lastTsRef.current == null) lastTsRef.current = ts
			const elapsed = ts - lastTsRef.current
			if (elapsed >= frameIntervalMs) {
				lastTsRef.current = ts
				setFrame((prev) => (prev + 1) % (Number(totalFrames) || 1))
			}
			// Only continue if still mounted and playing
			if (mountedRef.current && playingRef.current) {
				rafRef.current = requestAnimationFrame(tick)
			}
		},
		[frameIntervalMs, totalFrames]
	)

	const pause = useCallback(() => {
		playingRef.current = false
		if (rafRef.current != null) {
			cancelAnimationFrame(rafRef.current)
			rafRef.current = null
		}
		lastTsRef.current = null
	}, [])

	// Drag handling with improved logic (now that pause is defined)
	const dragHandlers = useDrag360(
		pixelsPerFrame,
		Number(totalFrames) || 1,
		handleFrameChange,
		pause
	)

	const play = useCallback(() => {
		if (!mountedRef.current || playingRef.current) return
		// Cancel any existing animation before starting new one
		if (rafRef.current != null) {
			cancelAnimationFrame(rafRef.current)
			rafRef.current = null
		}
		playingRef.current = true
		if (loaded && mountedRef.current) {
			rafRef.current = requestAnimationFrame(tick)
		}
	}, [tick, loaded])

	const toggle = useCallback(() => {
		if (playingRef.current) pause()
		else play()
	}, [play, pause])

	const goToFrame = useCallback(
		(idx: number) => {
			const safeFrameCount = Number(totalFrames) || 1
			const clamped = ((idx % safeFrameCount) + safeFrameCount) % safeFrameCount
			setFrame(clamped)
		},
		[totalFrames]
	)

	// Expose imperative API
	useImperativeHandle(
		ref,
		(): Spin360Handle => ({
			play,
			pause,
			toggle,
			goToFrame,
			currentFrame: () => frame,
			isPlaying: () => playingRef.current,
			isLoaded: () => loaded,
		}),
		[play, pause, toggle, goToFrame, frame, loaded]
	)

	// Start/stop autoplay on mount or when loaded changes
	useEffect(() => {
		if (autoplay && loaded && mountedRef.current) {
			play()
		} else if (!autoplay) {
			pause()
		}
	}, [autoplay, loaded, play, pause])

	// Component unmount cleanup
	useEffect(() => {
		return () => {
			mountedRef.current = false
			if (rafRef.current != null) {
				cancelAnimationFrame(rafRef.current)
				rafRef.current = null
			}
		}
	}, [])

	// Double-click toggles play/pause
	const onDoubleClick = useCallback(() => {
		toggle()
	}, [toggle])

	// Global mouse event handlers for proper drag behavior
	useEffect(() => {
		const handleGlobalMouseMove = (e: MouseEvent) => {
			dragHandlers.handleDragMove(e.clientX)
		}

		const handleGlobalMouseUp = () => {
			const wasDragging = dragHandlers.handleDragEnd()
			if (wasDragging) {
				justFinishedDragging.current = true
				setTimeout(() => {
					justFinishedDragging.current = false
				}, 100)
			}
		}

		// Add global listeners when component mounts
		document.addEventListener("mousemove", handleGlobalMouseMove)
		document.addEventListener("mouseup", handleGlobalMouseUp)

		return () => {
			document.removeEventListener("mousemove", handleGlobalMouseMove)
			document.removeEventListener("mouseup", handleGlobalMouseUp)
		}
	}, [dragHandlers])

	// Simplified mouse drag handlers
	const onMouseDown = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			dragHandlers.handleDragStart(e.clientX)
		},
		[dragHandlers]
	)

	// Local mouse handlers (global ones handle the actual drag)
	const onMouseUp = useCallback(() => {
		// Local mouse up - global handler will take care of drag end
	}, [])

	const onMouseLeave = useCallback(() => {
		// Local mouse leave - global handler will take care of drag end
	}, [])

	const onMouseMove = useCallback(() => {
		// Local mouse move - global handler will take care of drag move
	}, [])

	// Simplified touch drag handlers
	const onTouchStart = useCallback(
		(e: React.TouchEvent<HTMLDivElement>) => {
			if (e.touches.length !== 1) return
			dragHandlers.handleDragStart(e.touches[0].clientX)
			e.preventDefault()
			e.stopPropagation()
		},
		[dragHandlers]
	)

	const onTouchEnd = useCallback(
		(e: React.TouchEvent<HTMLDivElement>) => {
			const wasDragging = dragHandlers.handleDragEnd()
			// If we were dragging, set flag to prevent click
			if (wasDragging) {
				justFinishedDragging.current = true
				setTimeout(() => {
					justFinishedDragging.current = false
				}, 100)
			}
			e.preventDefault()
			e.stopPropagation()
		},
		[dragHandlers]
	)

	const onTouchMove = useCallback(
		(e: React.TouchEvent<HTMLDivElement>) => {
			if (e.touches.length !== 1) return
			dragHandlers.handleDragMove(e.touches[0].clientX)
			e.preventDefault()
			e.stopPropagation()
		},
		[dragHandlers]
	)

	// Keyboard navigation with consistent frame direction
	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.code === "Space") {
				e.preventDefault()
				toggle()
			} else if (e.code === "ArrowRight") {
				e.preventDefault()
				pause()
				handleFrameChange(1) // Forward one frame
			} else if (e.code === "ArrowLeft") {
				e.preventDefault()
				pause()
				handleFrameChange(-1) // Backward one frame
			}
		},
		[pause, toggle, handleFrameChange]
	)

	// Early return if invalid inputs - after all hooks to avoid Rules of Hooks violation
	if (!hasValidInputs) {
		console.warn("Spin360: Missing required props (meta or imageUrl) or invalid meta data")
		return null
	}

	// Background image selection: placeholder until loaded
	const backgroundImage =
		loaded ? `url("${imageUrl}")`
		: placeholderUrl ? `url("${placeholderUrl}")`
		: "none"

	// If using a horizontal placeholder (single row), we still want the correct bgSize.
	// The visual result with placeholder is just a blurred static image; frame stepping still updates bg-position.
	const containerStyle: CSSProperties = {
		width: resolvedWidth,
		height: resolvedHeight,
		backgroundImage,
		backgroundRepeat: "no-repeat",
		backgroundSize: bgSize,
		backgroundPosition: bgPosition,
		cursor: "grab",
		userSelect: "none",
		touchAction: "none",
		// subtle blur while not loaded if requested and placeholder provided
		filter: !loaded && placeholderUrl && blurUntilLoaded ? "blur(8px)" : undefined,
		transition: "filter 200ms ease",
		...style,
	}

	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		// Prevent click if we just finished dragging
		if (justFinishedDragging.current) {
			e.preventDefault()
			e.stopPropagation()
			return
		}

		if (onClick) {
			onClick(e)
		}
	}

	// Show skeleton while loading if enabled
	if (!loaded && showSkeleton) {
		return (
			<div
				role="button"
				aria-label={ariaLabel}
				className={className}
				style={{
					width: resolvedWidth,
					height: resolvedHeight,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					...style,
				}}
				tabIndex={0}
			>
				<Skeleton
					variant="circle"
					height={resolvedHeight}
					width={resolvedWidth}
					animation="wave"
				/>
			</div>
		)
	}

	return (
		<div
			role="button"
			aria-label={ariaLabel}
			className={className}
			style={containerStyle}
			tabIndex={0}
			onClick={handleClick}
			onDoubleClick={onDoubleClick}
			onKeyDown={onKeyDown}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			onMouseLeave={onMouseLeave}
			onMouseMove={onMouseMove}
			onTouchStart={onTouchStart}
			onTouchEnd={onTouchEnd}
			onTouchMove={onTouchMove}
		/>
	)
})
